import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import ProductCard from '../components/products/ProductCard';
import { products, categories, siteImages } from '../data/mock';
import { ChevronDown, LayoutGrid, Truck, ChevronRight } from 'lucide-react';

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortBy, setSortBy] = useState('featured');
  const selectedCategory = searchParams.get('category') || '';

  useEffect(() => { document.title = 'Shop Hardware & Power Tools | Ironworks Tools'; }, []);

  const filteredProducts = useMemo(() => {
    let result = [...products];
    if (selectedCategory) result = result.filter(p => p.categorySlug === selectedCategory);
    switch (sortBy) {
      case 'price-low': result.sort((a, b) => a.price - b.price); break;
      case 'price-high': result.sort((a, b) => b.price - a.price); break;
      case 'name': result.sort((a, b) => a.name.localeCompare(b.name)); break;
      default: break;
    }
    return result;
  }, [selectedCategory, sortBy]);

  const handleCategoryChange = (slug) => { if (slug) setSearchParams({ category: slug }); else setSearchParams({}); };
  const currentCategory = categories.find(c => c.slug === selectedCategory);

  const catBtn = (active) => `w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-sm font-medium text-left transition-colors ${active ? 'bg-athletic-600 text-white' : 'text-dark-700 hover:bg-athletic-50 hover:text-athletic-700'}`;

  return (
    <div className="min-h-screen bg-background text-dark-700">
      <Header />
      <CartDrawer />
      <main>
        {/* Calm banner */}
        <section className="relative overflow-hidden border-b border-dark-200">
          <img src={siteImages.shopBanner} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/50" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
            <nav className="flex items-center gap-1.5 text-sm text-dark-500 mb-3"><Link to="/" className="hover:text-athletic-700">Home</Link><ChevronRight className="w-4 h-4 text-dark-300" /><span className="text-athletic-700 font-medium">{currentCategory ? currentCategory.name : 'All Tools'}</span></nav>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold text-dark-900">{currentCategory ? currentCategory.name : 'The full tool catalog'}</h1>
            <p className="mt-3 text-dark-600 max-w-2xl">{currentCategory ? currentCategory.description : 'From laser range finders to cordless power tools and rugged storage — carefully chosen hardware that earns its place in your kit.'}</p>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-12 gap-8">
          {/* Sidebar */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-28 space-y-6">
              <div className="bg-white rounded-2xl border border-dark-200 p-4">
                <h2 className="font-display text-lg font-semibold text-dark-900 px-2 mb-3">Categories</h2>
                <div className="space-y-1">
                  <button onClick={() => handleCategoryChange('')} className={catBtn(!selectedCategory)}><span>All Tools</span><span className="text-xs opacity-70">{products.length}</span></button>
                  {categories.map((cat) => (
                    <button key={cat.id} onClick={() => handleCategoryChange(cat.slug)} className={catBtn(selectedCategory === cat.slug)}><span>{cat.name}</span><span className="text-xs opacity-70">{cat.productCount}</span></button>
                  ))}
                </div>
              </div>
              <div className="bg-athletic-50 rounded-2xl border border-athletic-100 p-5">
                <Truck className="w-6 h-6 text-athletic-700 mb-2" />
                <p className="font-display font-semibold text-dark-900 text-base">Free USPS shipping</p>
                <p className="text-sm text-dark-600 mt-1">On every order, delivered in 2&ndash;4 business days with 30-day returns.</p>
              </div>
            </div>
          </aside>

          {/* Grid */}
          <div className="lg:col-span-9">
            <div className="flex items-center justify-between gap-3 mb-6">
              <p className="flex items-center gap-2 text-sm text-dark-600"><LayoutGrid className="w-4 h-4 text-athletic-600" /><span className="font-semibold text-dark-900">{filteredProducts.length}</span> item{filteredProducts.length !== 1 ? 's' : ''}</p>
              <div className="relative">
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="appearance-none bg-white border border-dark-200 text-dark-700 text-sm font-medium rounded-xl px-4 py-2.5 pr-9 focus:outline-none focus:ring-2 focus:ring-athletic-500 cursor-pointer">
                  <option value="featured">Featured</option><option value="price-low">Price: Low to High</option><option value="price-high">Price: High to Low</option><option value="name">Name: A-Z</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400 pointer-events-none" />
              </div>
            </div>

            {filteredProducts.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-dark-200"><p className="text-dark-600">No tools found in this category.</p></div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">{filteredProducts.map((p) => (<ProductCard key={p.id} product={p} />))}</div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ShopPage;
